import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { CompanyRelationship } from '@/utils/companyRelationship';

export interface NewCompanyPayload {
	name: string;
	assignee?: string;
	street?: string;
	postal_code?: string;
	city?: string;
	country?: string;
	vat_number?: string;
	website_url?: string;
	logo_url?: string;
	relationship?: CompanyRelationship;
	custom_fields?: Record<string, any>;
}

export interface CreateCompanyResult {
	success: boolean;
	id?: string;
}

export const useCreateCompany = () => {
	const { t } = useTranslation();
	const [creating, setCreating] = useState(false);

	const createCompany = async (payload: NewCompanyPayload): Promise<CreateCompanyResult> => {
		setCreating(true);
		try {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) throw new Error('User not authenticated');

			const { data: userOrg } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();

			if (!userOrg) throw new Error('User organization not found');

			const insertRow: Record<string, any> = {
				name: payload.name,
				organization_uuid: userOrg.organization_uuid,
			};
			if (payload.assignee) insertRow.assignee = payload.assignee;
			if (payload.street) insertRow.street = payload.street;
			if (payload.postal_code) insertRow.postal_code = payload.postal_code;
			if (payload.city) insertRow.city = payload.city;
			if (payload.country) insertRow.country = payload.country;
			if (payload.vat_number) insertRow.vat_number = payload.vat_number;
			if (payload.website_url) insertRow.website_url = payload.website_url;
			if (payload.logo_url) insertRow.logo_url = payload.logo_url;
			if (payload.relationship && payload.relationship !== 'none') insertRow.relationship = payload.relationship;

			const { data, error } = await supabase
				.from('companies')
				.insert(insertRow)
				.select('id')
				.single();

			if (error) throw error;

			toast({
				title: t('common.success'),
				description: t('companies.addSuccess'),
			});

			return { success: true, id: data.id };
		} catch (err) {
			console.error('Error creating company:', err);
			toast({
				title: t('common.error'),
				description: err instanceof Error ? err.message : 'Failed to create company',
				variant: 'destructive',
			});
			return { success: false };
		} finally {
			setCreating(false);
		}
	};

	return { createCompany, creating };
};
